import React from "react";
import { VALIDATION } from "../../../constants";
import { SessionFormState } from "../../../utils/sessionFormState";

type Props = {
  form: SessionFormState;
  setField: <K extends keyof SessionFormState>(
    key: K,
    value: SessionFormState[K]
  ) => void;
};

const ContentStep: React.FC<Props> = ({ form, setField }) => {
  const takes = form.takeaways;
  const filled = takes.filter((t) => t.trim()).length;

  const updateTakeaway = (index: number, value: string) => {
    const next = [...takes];
    next[index] = value.slice(0, VALIDATION.takeawayCharMax);
    setField("takeaways", next);
  };

  const addTakeaway = () => {
    if (takes.length >= VALIDATION.takeawayMax) return;
    setField("takeaways", [...takes, ""]);
  };

  const removeTakeaway = (index: number) => {
    if (takes.length <= 1) return;
    setField(
      "takeaways",
      takes.filter((_, i) => i !== index)
    );
  };

  const fullLen = form.fullDesc.length;
  const fullCountFg =
    fullLen > VALIDATION.fullDescMax ||
    (fullLen > 0 && fullLen < VALIDATION.fullDescMin)
      ? "#B4483A"
      : fullLen >= VALIDATION.fullDescMin
      ? "#238A5E"
      : "#918A82";

  return (
    <div className="wf-step">
      <div className="wf-field">
        <div className="wf-field-head">
          <label>
            Full description <span className="req">*</span>
          </label>
          <span className="count" style={{ color: fullCountFg }}>
            {fullLen} / {VALIDATION.fullDescMax}
          </span>
        </div>
        <textarea
          className="wf-textarea"
          value={form.fullDesc}
          maxLength={VALIDATION.fullDescMax}
          onChange={(e) => setField("fullDesc", e.target.value)}
          placeholder="Plain paragraphs. What happens in the session, who it is for, what people leave with."
          rows={6}
        />
        <div className="wf-field__hint">
          40–600 characters. Plain paragraphs, no external links.
        </div>
      </div>

      <div>
        <div className="wf-field-head" style={{ marginBottom: 4 }}>
          <label>
            What you&apos;ll take away <span className="req">*</span>
          </label>
          <span
            className="count"
            style={{
              color: filled < 3 ? "#9A7A3A" : "#238A5E",
            }}
          >
            {filled} / {VALIDATION.takeawayMax} added
          </span>
        </div>
        <div
          className="wf-field__hint"
          style={{ marginBottom: 10, marginTop: 0 }}
        >
          Three to five outcomes, phrased as what the member will be able to do.
          Each up to 90 characters.
        </div>

        <div className="wf-takeaways">
          {takes.map((t, i) => (
            <div key={i} className="wf-takeaways__row">
              <span className="wf-takeaways__num">{i + 1}</span>
              <input
                type="text"
                value={t}
                maxLength={VALIDATION.takeawayCharMax}
                placeholder="You will be able to …"
                onChange={(e) => updateTakeaway(i, e.target.value)}
              />
              <span
                className="wf-takeaways__count"
                style={{
                  color:
                    t.length > VALIDATION.takeawayCharMax
                      ? "#B4483A"
                      : "#918A82",
                }}
              >
                {t.length}
              </span>
              <button
                type="button"
                className="wf-takeaways__remove"
                aria-label="Remove takeaway"
                style={{ opacity: takes.length <= 1 ? 0.35 : 1 }}
                disabled={takes.length <= 1}
                onClick={() => removeTakeaway(i)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="wf-takeaways__add"
          disabled={takes.length >= VALIDATION.takeawayMax}
          onClick={addTakeaway}
          style={{
            opacity: takes.length >= VALIDATION.takeawayMax ? 0.4 : 1,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add takeaway
        </button>
      </div>

      <div className="wf-tip">
        <svg
          width="17"
          height="17"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9A7A3A"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M12 3l9.5 17H2.5z" />
          <path d="M12 9.5v4.5M12 16.8v.2" />
        </svg>
        <p>
          Anything that reads as a clinical claim is flagged for review before
          this publishes. Keep takeaways outcome-phrased — “you will be able to
          …”.
        </p>
      </div>
    </div>
  );
};

export default ContentStep;
